<template>
  <div class="parking-area-manage">
    <div class="page-content">
      <!-- 页面标题 -->
      <div class="page-title">{{ $t('parkingAreaManage.parkingInfo') }}</div>

      <!-- 工具栏 -->
      <div class="toolbar">
        <el-button class="add-btn" @click="_openAddParkingAreaModal">
          <i class="el-icon-plus"></i>
          {{ $t('common.add') }}
        </el-button>
        <el-button class="icon-reset-btn" @click="_resetParkingAreaMethod">
          <i class="el-icon-refresh-right"></i>
        </el-button>

        <div class="filter-group">
          <el-input v-model.trim="parkingAreaManageInfo.conditions.num"
            :placeholder="$t('parkingAreaManage.inputParkingNum')" class="filter-input" clearable
            @keyup.enter.native="_queryParkingAreaMethod" />
          <el-select v-model="parkingAreaManageInfo.conditions.typeCd"
            :placeholder="$t('parkingAreaManage.selectParkingType')" class="filter-select" clearable>
            <el-option v-for="item in parkingTypes" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </div>

        <div class="toolbar-right">
          <el-button class="search-btn" @click="_queryParkingAreaMethod">
            <i class="el-icon-search"></i>
            {{ $t('common.search') }}
          </el-button>
        </div>
      </div>

      <!-- 列表 -->
      <el-table :data="parkingAreaManageInfo.parkingAreas" stripe class="data-table" v-loading="loading">
        <el-table-column prop="communityName" label="所在小区" align="center" min-width="228"></el-table-column>
        <el-table-column prop="paId" :label="$t('parkingAreaManage.parkingId')" align="center"
          min-width="212"></el-table-column>
        <el-table-column prop="num" :label="$t('parkingAreaManage.parkingNum')" align="center"
          min-width="280"></el-table-column>
        <el-table-column :label="$t('parkingAreaManage.parkingType')" align="center" min-width="240">
          <template slot-scope="scope">
            {{ scope.row.typeCd === '1001' ? $t('parkingAreaManage.aboveGround') :
              $t('parkingAreaManage.underground')
            }}
          </template>
        </el-table-column>
        <el-table-column v-for="(item, index) in parkingAreaManageInfo.listColumns" :key="index"
          :label="item.specName" align="center" min-width="200">
          <template slot-scope="scope">
            {{ scope.row.listValues[index] }}
          </template>
        </el-table-column>
        <el-table-column prop="remark" :label="$t('parkingAreaManage.remark')" align="center"
          min-width="280"></el-table-column>
        <el-table-column prop="createTime" :label="$t('parkingAreaManage.createTime')" align="center"
          min-width="280"></el-table-column>
        <el-table-column :label="$t('common.operation')" align="center" min-width="150">
          <template slot-scope="scope">
            <el-button type="text" class="action-btn edit-btn"
              @click="_openEditParkingAreaModel(scope.row)">
              {{ $t('common.edit') }}
            </el-button>
            <el-button type="text" class="action-btn delete-btn"
              @click="_openDeleteParkingAreaModel(scope.row)">
              {{ $t('common.delete') }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <el-pagination :current-page.sync="pagination.current" :page-sizes="[10, 20, 30, 50]"
        :page-size="pagination.size" :total="pagination.total" layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange" @current-change="handleCurrentChange" class="pagination-bar" />
    </div>

    <add-parking-area ref="addModal" @success="handleAddSuccess" />
    <edit-parking-area ref="editModal" @success="handleEditSuccess" />
    <delete-parking-area ref="deleteModal" @success="handleDeleteSuccess" />
  </div>
</template>

<script>
import { getParkingAreaList } from '@/api/car/parkingAreaManageApi'
import AddParkingArea from '@/components/car/addParkingArea'
import EditParkingArea from '@/components/car/editParkingArea'
import DeleteParkingArea from '@/components/car/deleteParkingArea'

export default {
  name: 'ParkingAreaManageList',
  components: {
    AddParkingArea,
    EditParkingArea,
    DeleteParkingArea
  },
  data() {
    return {
      loading: false,
      parkingAreaManageInfo: {
        parkingAreas: [],
        listColumns: [],
        conditions: {
          num: '',
          typeCd: '',
          paId: ''
        }
      },
      pagination: {
        current: 1,
        size: 10,
        total: 0
      },
      parkingTypes: [
        { value: '1001', label: this.$t('parkingAreaManage.aboveGround') },
        { value: '2001', label: this.$t('parkingAreaManage.underground') }
      ]
    }
  },
  created() {
    this._getColumns()
    this._listParkingAreas()
  },
  methods: {
    async _listParkingAreas() {
      this.loading = true
      try {
        const params = {
          page: this.pagination.current,
          row: this.pagination.size,
          ...this.parkingAreaManageInfo.conditions
        }

        const res = await getParkingAreaList(params)
        this.parkingAreaManageInfo.parkingAreas = res.parkingAreas
        this.pagination.total = res.total
        this.dealParkingAreaAttr(res.parkingAreas)
      } catch (error) {
        console.error('获取停车场列表失败:', error)
        this.$message.error(this.$t('parkingAreaManage.fetchError'))
      } finally {
        this.loading = false
      }
    },

    _queryParkingAreaMethod() {
      this.pagination.current = 1
      this._listParkingAreas()
    },

    _resetParkingAreaMethod() {
      this.parkingAreaManageInfo.conditions = {
        num: '',
        typeCd: '',
        paId: ''
      }
      this._listParkingAreas()
    },

    _openAddParkingAreaModal() {
      this.$refs.addModal.open()
    },

    _openEditParkingAreaModel(row) {
      this.$refs.editModal.open(row)
    },

    _openDeleteParkingAreaModel(row) {
      this.$refs.deleteModal.open(row)
    },

    handleAddSuccess() {
      this._listParkingAreas()
    },

    handleEditSuccess() {
      this._listParkingAreas()
    },

    handleDeleteSuccess() {
      this._listParkingAreas()
    },

    handleSizeChange(size) {
      this.pagination.size = size
      this._listParkingAreas()
    },

    handleCurrentChange(current) {
      this.pagination.current = current
      this._listParkingAreas()
    },

    dealParkingAreaAttr(parkingAreas) {
      parkingAreas.forEach(item => {
        this._getColumnsValue(item)
      })
    },

    _getColumnsValue(parkingArea) {
      parkingArea.listValues = []
      if (!parkingArea.attrs || parkingArea.attrs.length === 0) {
        this.parkingAreaManageInfo.listColumns.forEach(() => {
          parkingArea.listValues.push('')
        })
        return
      }

      this.parkingAreaManageInfo.listColumns.forEach(column => {
        const attr = parkingArea.attrs.find(a => a.specCd === column.specCd)
        parkingArea.listValues.push(attr ? attr.value : '')
      })
    },

    async _getColumns() {
      try {
        // 这里需要根据实际接口获取列配置
        // 示例代码，实际需要替换为API调用
        this.parkingAreaManageInfo.listColumns = [
        ]
      } catch (error) {
        console.error('获取列配置失败:', error)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  .parking-area-manage {
    padding: 20px;
    background-color: #EBF2FA;
    min-height: calc(100vh - 84px);
    font-family: 'PingFang SC', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif;
  }

  .page-content {
    background: #FFFFFF;
    border-radius: 12px;
    padding: 22px 24px;
  }

  .page-title {
    font-size: 16px;
    font-family: 'PingFang SC', sans-serif;
    font-weight: 600;
    color: #1D2129;
    padding-bottom: 20px;
    border-bottom: 1px solid #DCE6F5;
    margin-bottom: 20px;
    text-align: left;
  }

  .toolbar {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 24px;

    .add-btn {
      width: 84px;
      height: 38px;
      border-radius: 10px;
      background: #0E76FF;
      border: none;
      color: #FFFFFF;
      font-family: 'Inter', 'PingFang SC', sans-serif;
      font-weight: 500;
      font-size: 14px;
      padding: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 4px;

      &:hover {
        background: #1C99FF;
      }

      i {
        font-size: 12px;
      }
    }

    .icon-reset-btn {
      width: 38px;
      height: 38px;
      border-radius: 10px;
      border: 1px solid #DCE6F5;
      background: #FFFFFF;
      color: #8C9BB4;
      padding: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;

      i {
        font-size: 14px;
      }

      &:hover {
        color: #0E76FF;
        border-color: #0E76FF;
      }
    }

    .filter-group {
      display: flex;
      align-items: center;
      gap: 12px;
      flex: 1;
      justify-content: flex-end;

      .filter-input,
      .filter-select {
        width: 256px;

        ::v-deep .el-input__inner {
          border-radius: 10px;
          border: 1px solid #DCE6F5;
          background: #FFFFFF;
          font-family: 'Inter', 'PingFang SC', sans-serif;
          font-size: 14px;
          color: #1D2129;
          height: 38px;
          line-height: 38px;

          &::placeholder {
            color: #8C9BB4;
          }
        }
      }
    }

    .toolbar-right {
      display: flex;
      align-items: center;
      gap: 12px;

      .search-btn {
        width: 84px;
        height: 38px;
        border-radius: 10px;
        border: 1px solid #0E76FF;
        background: #FFFFFF;
        color: #0E76FF;
        font-family: 'Inter', 'PingFang SC', sans-serif;
        font-weight: 500;
        font-size: 14px;
        padding: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 4px;

        &:hover {
          background: #E8F3FF;
        }

        i {
          font-size: 12px;
        }
      }
    }
  }

  .data-table {
    border-radius: 10px;
    overflow: hidden;

    &::before {
      display: none;
    }

    ::v-deep .el-table__header-wrapper {
      th {
        background-color: #EBF2FA !important;
        color: #364153 !important;
        font-weight: 500;
        font-size: 14px;
        font-family: 'PingFang SC', sans-serif;
        padding: 12px 0;
        height: 50px;
      }
    }

    ::v-deep .el-table__row {
      height: 50px;
      font-size: 14px;
      font-family: 'PingFang SC', sans-serif;
      color: #1D2129;

      td {
        padding: 12px 0;
      }
    }

    ::v-deep .el-table__body tr:nth-child(even) td {
      background-color: #F8FBFF !important;
    }

    ::v-deep .el-table__body tr:hover td {
      background-color: #F5F9FF !important;
    }
  }

  .action-btn {
    font-size: 14px;
    font-family: 'PingFang SC', sans-serif;
    font-weight: 400;
    padding: 0 8px;

    &.edit-btn {
      color: #0E76FF;
    }

    &.delete-btn {
      color: #FB2C36;
    }

    &.control-btn {
      color: #21B956;
    }
  }

  .pagination-bar {
    margin-top: 24px;
    display: flex;
    justify-content: flex-end;

    ::v-deep .el-pagination__total {
      font-size: 14px;
      color: #1D2129;
      font-family: 'PingFang SC', sans-serif;
    }

    ::v-deep .el-pager li {
      font-size: 14px;
      font-family: 'Nunito Sans', sans-serif;
      font-weight: 600;
      border-radius: 8px;
      min-width: 32px;
      height: 32px;
      line-height: 32px;

      &.active {
        background: #E8F3FF;
        color: #0E76FF;
      }
    }
  }
</style>
